import type { IdNameAddressResult, LocationType } from '@/api/useMetadata/types'
import { useLocations } from '@/api/useMetadata/useMasterMetadata'
import { Texto } from '@gravitate-js/excalibrr'
import { Divider, Select } from 'antd'
import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { useScheduleContext } from '../Contexts/ScheduleContext'

const { Option } = Select

export function Section({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <div className='link-wizard-section'>
      <Texto category='h4'>{title}</Texto>
      <Divider className='title-divider' />
      {children}
    </div>
  )
}

function formatLabel({ name, address, city, state }: IdNameAddressResult) {
  const addr = [address, city, state].filter(Boolean).join(', ')
  return addr ? `${name} (${addr})` : name
}

export function StartingLocationInputs(props: {
  value?: string
  onChange?: (v?: string) => void
  onInputTypeChange?: (v: LocationType) => void
}) {
  const [inputType, _setInputType] = useState<LocationType>('Depot')
  const { selectedDepots, setSelectedDepots } = useScheduleContext() as {
    selectedDepots?: string[]
    setSelectedDepots: (v?: string[]) => void
  }

  const { data, isLoading } = useLocations(inputType)
  const options = useMemo(
    () =>
      data?.results?.map((x) => ({
        value: x.id,
        label: formatLabel(x),
      })),
    [data]
  )
  useEffect(() => {
    props.onInputTypeChange?.(inputType)
  }, [inputType, props.onInputTypeChange])

  const onChange = useCallback(
    (v?: string) => {
      if (inputType === 'Depot') {
        const depot = data?.results?.find((x) => x.id === v)
        if (depot) {
          setSelectedDepots([depot.name])
        }
      }
      props.onChange?.(v)
    },
    [props.onChange, inputType, data, setSelectedDepots]
  )

  const setInputType = useCallback(
    (value: LocationType) => {
      _setInputType(value)
      props.onInputTypeChange?.(value)
      onChange()
    },
    [props.onInputTypeChange, onChange]
  )

  useEffect(() => {
    if (
      inputType !== 'Depot' ||
      !selectedDepots?.length ||
      !data?.results?.length ||
      !!props.value ||
      !props.onChange
    ) {
      return
    }
    const defaultDepot = data.results.find((x) => x.name === selectedDepots[0])
    if (defaultDepot) {
      props.onChange(defaultDepot.id)
    }
  }, [selectedDepots, inputType, data, props.value, props.onChange])
  return (
    <div className='wizard-start-location'>
      <Select className='location-type-input' value={inputType} onChange={(v) => setInputType(v)}>
        <Option value='Depot'>Depot</Option>
        <Option value='Store'>Site</Option>
        <Option value='Terminal'>Terminal</Option>
      </Select>
      <Select
        optionFilterProp='label'
        loading={isLoading}
        placeholder='Select location'
        className='location-input'
        {...props}
        onChange={onChange}
        options={options}
        showSearch
      />
    </div>
  )
}
